import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Plus, Trash2, ArrowUp, ArrowDown, Loader2, Wand2, Image as ImageIcon,
  ChevronDown, ChevronRight,
} from "lucide-react";

import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Button } from "@/components/ui/button";

/**
 * Phase 2 collection editors — used from RightPanel.jsx when the selected
 * anchor's `type` is one of:
 *   • products  → `<ProductsEditor/>` — CRUD via /api/ecommerce/products
 *   • menu      → `<MenuEditor/>`     — PATCH /business/website with `menu` list
 *   • services  → `<ServicesEditor/>` — PATCH /business/website with `services` list
 *
 * All three follow the same UX pattern:
 *   1. Load the current items (products via API; menu/services from
 *      publicPayload.website_content) into local state.
 *   2. Render each item as a collapsible card with inline text fields +
 *      image picker + move up / move down / delete buttons.
 *   3. `Save all` writes the new list back to the server. For products,
 *      this is a per-item diff of PUT/POST/DELETE calls. For menu/services
 *      we send the whole array via a single PATCH.
 *   4. On success we bump the parent's canvas via `onCanvasChange()` so the
 *      live preview updates.
 */

/* -------------------------------------------------------------------------- */
/* ProductsEditor                                                             */
/* -------------------------------------------------------------------------- */

export function ProductsEditor({ bizId, onCanvasChange }) {
  const [products, setProducts] = useState(null); // null = loading, [] = loaded
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);
  const originalRef = useRef([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get(`/ecommerce/products?business_id=${bizId}`);
        if (cancelled) return;
        const list = (data.products || []).map((p) => ({ ...p, _clientId: p.product_id }));
        setProducts(list);
        originalRef.current = list;
      } catch {
        setProducts([]);
      }
    })();
    return () => { cancelled = true; };
  }, [bizId]);

  const mutate = (updater) => {
    setProducts((prev) => {
      const next = updater(prev || []);
      setDirty(true);
      return next;
    });
  };

  const addNew = () => {
    const id = `new_${Date.now()}`;
    mutate((list) => [
      ...list,
      {
        _clientId: id,
        _isNew: true,
        name: "New product",
        price_cents: 1000,
        currency: "usd",
        description: "",
        image_url: "",
        kind: "one_time",
        active: true,
      },
    ]);
  };

  const remove = (id) => mutate((list) => list.filter((p) => p._clientId !== id));
  const move = (id, dir) => mutate((list) => {
    const idx = list.findIndex((p) => p._clientId === id);
    if (idx < 0) return list;
    const target = idx + dir;
    if (target < 0 || target >= list.length) return list;
    const next = [...list];
    [next[idx], next[target]] = [next[target], next[idx]];
    return next;
  });

  const patch = (id, keyOrObj, value) => mutate((list) => list.map((p) => {
    if (p._clientId !== id) return p;
    return { ...p, ...(typeof keyOrObj === "object" ? keyOrObj : { [keyOrObj]: value }) };
  }));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      const existing = originalRef.current;
      const existingById = new Map(existing.map((p) => [p.product_id, p]));
      const currentIds = new Set(products.filter((p) => !p._isNew).map((p) => p.product_id));
      // 1. Delete removed rows.
      for (const orig of existing) {
        if (!currentIds.has(orig.product_id)) {
          await api.delete(`/ecommerce/products/${orig.product_id}?business_id=${bizId}`);
        }
      }
      // 2. Update changed rows + create new rows in the current order.
      for (const p of products) {
        const cleanBody = {
          name: p.name,
          price_cents: parseInt(p.price_cents, 10) || 0,
          currency: p.currency || "usd",
          description: p.description || "",
          image_url: p.image_url || "",
          kind: p.kind || "one_time",
          active: p.active !== false,
        };
        if (p._isNew) {
          await api.post(`/ecommerce/products?business_id=${bizId}`, cleanBody);
        } else {
          const orig = existingById.get(p.product_id);
          if (!shallowEqual(orig, p, Object.keys(cleanBody))) {
            await api.put(`/ecommerce/products/${p.product_id}?business_id=${bizId}`, cleanBody);
          }
        }
      }
      // 3. Refresh from server.
      const { data } = await api.get(`/ecommerce/products?business_id=${bizId}`);
      const fresh = (data.products || []).map((p) => ({ ...p, _clientId: p.product_id }));
      setProducts(fresh);
      originalRef.current = fresh;
      setDirty(false);
      onCanvasChange?.();
      toast.success("Products saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save products");
    } finally {
      setSaving(false);
    }
  };

  if (products === null) return <Loading />;

  return (
    <div className="space-y-3">
      <EditorHeader
        title="Products"
        subtitle="Add, edit, reorder, or remove products shown in your storefront."
        onSaveAll={save}
        dirty={dirty}
        saving={saving}
      />

      <div className="space-y-2">
        {products.map((p, i) => (
          <ItemCard
            key={p._clientId}
            title={p.name || "Untitled product"}
            subtitle={p.price_cents ? `$${(parseInt(p.price_cents, 10) / 100).toFixed(2)}` : ""}
            isFirst={i === 0}
            isLast={i === products.length - 1}
            onMoveUp={() => move(p._clientId, -1)}
            onMoveDown={() => move(p._clientId, 1)}
            onDelete={() => remove(p._clientId)}
            testId={`product-item-${p._clientId}`}
          >
            <FieldRow label="Product name">
              <Input value={p.name || ""} onChange={(e) => patch(p._clientId, "name", e.target.value)} data-testid={`product-name-${p._clientId}`} />
            </FieldRow>
            <div className="grid grid-cols-2 gap-2">
              <FieldRow label="Price ($)">
                <Input
                  type="number"
                  step="0.01"
                  value={((parseInt(p.price_cents, 10) || 0) / 100).toFixed(2)}
                  onChange={(e) => patch(p._clientId, "price_cents", Math.round(parseFloat(e.target.value || 0) * 100))}
                  data-testid={`product-price-${p._clientId}`}
                />
              </FieldRow>
              <FieldRow label="Category">
                <Input value={p.category || ""} onChange={(e) => patch(p._clientId, "category", e.target.value)} data-testid={`product-category-${p._clientId}`} />
              </FieldRow>
            </div>
            <FieldRow label="Description">
              <Textarea rows={2} value={p.description || ""} onChange={(e) => patch(p._clientId, "description", e.target.value)} data-testid={`product-desc-${p._clientId}`} />
            </FieldRow>
            <FieldRow label="Product image URL">
              <ImageFieldWithPreview
                value={p.image_url}
                onChange={(v) => patch(p._clientId, "image_url", v)}
                testId={`product-image-${p._clientId}`}
              />
            </FieldRow>
          </ItemCard>
        ))}
      </div>

      <AddNewBtn label="Add product" onClick={addNew} testId="products-add-new" />
    </div>
  );
}

function shallowEqual(a, b, keys) {
  if (!a || !b) return false;
  return keys.every((k) => String(a[k] ?? "") === String(b[k] ?? ""));
}

/* -------------------------------------------------------------------------- */
/* MenuEditor                                                                 */
/* -------------------------------------------------------------------------- */

export function MenuEditor({ publicPayload, updateField, saveAll, onCanvasChange }) {
  // Normalize incoming menu — historical shapes vary. We coerce each
  // category into { title, subtitle, items: [{name, desc, price, image_url}] }.
  const normalize = (raw) => (raw || []).map((sec) => ({
    title: sec?.title ?? sec?.name ?? "",
    subtitle: sec?.subtitle ?? "",
    items: (sec?.items || []).map((it) => ({
      name: it?.name ?? "",
      desc: it?.desc ?? it?.description ?? "",
      price: (it?.price ?? "").toString().replace("$", ""),
      image_url: it?.image_url ?? it?.image ?? "",
    })),
  }));
  const [sections, setSections] = useState(() => normalize(publicPayload?.website_content?.menu));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setSections(normalize(publicPayload?.website_content?.menu));
    setDirty(false);
  }, [publicPayload?.website_content?.menu]);

  const mutate = (updater) => {
    setSections((prev) => {
      const next = updater(prev);
      setDirty(true);
      return next;
    });
  };

  const addSection = () => mutate((list) => [...list, { title: "New category", subtitle: "", items: [] }]);
  const removeSection = (i) => mutate((list) => list.filter((_, idx) => idx !== i));
  const moveSection = (i, dir) => mutate((list) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const patchSection = (i, k, v) => mutate((list) => list.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  const addItem = (si) => mutate((list) => list.map((s, i) => i === si ? { ...s, items: [...(s.items || []), { name: "New dish", desc: "", price: "" }] } : s));
  const removeItem = (si, ii) => mutate((list) => list.map((s, i) => i === si ? { ...s, items: s.items.filter((_, j) => j !== ii) } : s));
  const moveItem = (si, ii, dir) => mutate((list) => list.map((s, i) => {
    if (i !== si) return s;
    const items = [...(s.items || [])];
    const j = ii + dir;
    if (j < 0 || j >= items.length) return s;
    [items[ii], items[j]] = [items[j], items[ii]];
    return { ...s, items };
  }));
  const patchItem = (si, ii, k, v) => mutate((list) => list.map((s, i) => {
    if (i !== si) return s;
    return { ...s, items: (s.items || []).map((it, j) => j === ii ? { ...it, [k]: v } : it) };
  }));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await saveAll({ menu: sections });
      setDirty(false);
      onCanvasChange?.();
      toast.success("Menu saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save menu");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <EditorHeader
        title="Menu"
        subtitle="Organize your menu into categories with items, descriptions, and prices."
        onSaveAll={save}
        dirty={dirty}
        saving={saving}
      />

      <div className="space-y-3">
        {sections.map((sec, si) => (
          <ItemCard
            key={si}
            title={sec.title || "New category"}
            subtitle={`${(sec.items || []).length} items`}
            isFirst={si === 0}
            isLast={si === sections.length - 1}
            onMoveUp={() => moveSection(si, -1)}
            onMoveDown={() => moveSection(si, 1)}
            onDelete={() => removeSection(si)}
            testId={`menu-category-${si}`}
            defaultOpen
          >
            <FieldRow label="Category title">
              <Input value={sec.title || ""} onChange={(e) => patchSection(si, "title", e.target.value)} data-testid={`menu-category-title-${si}`} />
            </FieldRow>
            <FieldRow label="Category subtitle">
              <Input value={sec.subtitle || ""} onChange={(e) => patchSection(si, "subtitle", e.target.value)} />
            </FieldRow>
            <div>
              <div className="text-[11px] font-black uppercase tracking-wider text-gray-500 mb-1.5">Items</div>
              <div className="space-y-2">
                {(sec.items || []).map((it, ii) => (
                  <MiniItem
                    key={ii}
                    isFirst={ii === 0}
                    isLast={ii === (sec.items || []).length - 1}
                    onMoveUp={() => moveItem(si, ii, -1)}
                    onMoveDown={() => moveItem(si, ii, 1)}
                    onDelete={() => removeItem(si, ii)}
                    testId={`menu-item-${si}-${ii}`}
                  >
                    <div className="grid grid-cols-[1fr_80px] gap-2">
                      <Input value={it.name || ""} onChange={(e) => patchItem(si, ii, "name", e.target.value)} placeholder="Item name" className="text-sm" data-testid={`menu-item-name-${si}-${ii}`} />
                      <Input value={it.price || ""} onChange={(e) => patchItem(si, ii, "price", e.target.value)} placeholder="Price" className="text-sm" data-testid={`menu-item-price-${si}-${ii}`} />
                    </div>
                    <Textarea value={it.desc || ""} onChange={(e) => patchItem(si, ii, "desc", e.target.value)} placeholder="Description" rows={2} className="text-sm mt-1" data-testid={`menu-item-desc-${si}-${ii}`} />
                  </MiniItem>
                ))}
              </div>
              <button onClick={() => addItem(si)} className="mt-2 flex items-center gap-1.5 text-xs font-bold text-emerald-700 hover:text-emerald-800" data-testid={`menu-add-item-${si}`}>
                <Plus className="w-3 h-3" /> Add item
              </button>
            </div>
          </ItemCard>
        ))}
      </div>

      <AddNewBtn label="Add category" onClick={addSection} testId="menu-add-category" />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* ServicesEditor                                                             */
/* -------------------------------------------------------------------------- */

export function ServicesEditor({ publicPayload, saveAll, onCanvasChange }) {
  // Normalize the incoming shape — historical data used `title` while
  // newer entries use `name`; the SRC template renders whichever is
  // present. We always expose `name` in the editor state.
  const normalize = (raw) => (raw || []).map((s) => ({
    name: s?.name ?? s?.title ?? "",
    description: s?.description ?? s?.body ?? "",
    image_url: s?.image_url ?? s?.image ?? "",
    price: s?.price ?? "",
    bullets: Array.isArray(s?.bullets) ? s.bullets : [],
  }));
  const [items, setItems] = useState(() => normalize(publicPayload?.website_content?.services));
  const [dirty, setDirty] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    setItems(normalize(publicPayload?.website_content?.services));
    setDirty(false);
  }, [publicPayload?.website_content?.services]);

  const mutate = (updater) => setItems((prev) => { const next = updater(prev); setDirty(true); return next; });

  const add = () => mutate((list) => [...list, { name: "New service", description: "", image_url: "", price: "", bullets: [] }]);
  const remove = (i) => mutate((list) => list.filter((_, idx) => idx !== i));
  const move = (i, dir) => mutate((list) => {
    const j = i + dir;
    if (j < 0 || j >= list.length) return list;
    const next = [...list];
    [next[i], next[j]] = [next[j], next[i]];
    return next;
  });
  const patch = (i, k, v) => mutate((list) => list.map((s, idx) => idx === i ? { ...s, [k]: v } : s));

  const save = async () => {
    if (!dirty) return;
    setSaving(true);
    try {
      await saveAll({ services: items });
      setDirty(false);
      onCanvasChange?.();
      toast.success("Services saved.");
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't save services");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="space-y-3">
      <EditorHeader
        title="Services"
        subtitle="Add, edit, or reorder the services you offer."
        onSaveAll={save}
        dirty={dirty}
        saving={saving}
      />

      <div className="space-y-2">
        {items.map((s, i) => (
          <ItemCard
            key={i}
            title={s.name || "New service"}
            subtitle={s.price || ""}
            isFirst={i === 0}
            isLast={i === items.length - 1}
            onMoveUp={() => move(i, -1)}
            onMoveDown={() => move(i, 1)}
            onDelete={() => remove(i)}
            testId={`service-item-${i}`}
          >
            <FieldRow label="Service name">
              <Input value={s.name || ""} onChange={(e) => patch(i, "name", e.target.value)} data-testid={`service-name-${i}`} />
            </FieldRow>
            <FieldRow label="Description">
              <Textarea rows={3} value={s.description || ""} onChange={(e) => patch(i, "description", e.target.value)} data-testid={`service-desc-${i}`} />
            </FieldRow>
            <FieldRow label="Price (optional)">
              <Input value={s.price || ""} onChange={(e) => patch(i, "price", e.target.value)} placeholder="e.g. from $199" data-testid={`service-price-${i}`} />
            </FieldRow>
            <FieldRow label="Image URL">
              <ImageFieldWithPreview
                value={s.image_url}
                onChange={(v) => patch(i, "image_url", v)}
                testId={`service-image-${i}`}
              />
            </FieldRow>
          </ItemCard>
        ))}
      </div>

      <AddNewBtn label="Add service" onClick={add} testId="services-add-new" />
    </div>
  );
}

/* -------------------------------------------------------------------------- */
/* Shared UI primitives                                                       */
/* -------------------------------------------------------------------------- */

function Loading() {
  return (
    <div className="py-8 flex items-center justify-center">
      <Loader2 className="w-5 h-5 animate-spin text-gray-400" />
    </div>
  );
}

function EditorHeader({ title, subtitle, onSaveAll, dirty, saving }) {
  return (
    <div className="pb-3 border-b border-gray-100">
      <div className="flex items-center gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-sm font-black">{title}</div>
          <div className="text-[11px] text-gray-500 mt-0.5">{subtitle}</div>
        </div>
        <Button
          onClick={onSaveAll}
          disabled={!dirty || saving}
          size="sm"
          className="text-xs font-black bg-emerald-500 hover:bg-emerald-600 text-white disabled:opacity-50"
          data-testid="list-editor-save-all"
        >
          {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : dirty ? "Save all" : "Saved"}
        </Button>
      </div>
    </div>
  );
}

function ItemCard({ title, subtitle, isFirst, isLast, onMoveUp, onMoveDown, onDelete, children, defaultOpen, testId }) {
  const [open, setOpen] = useState(!!defaultOpen);
  return (
    <div className="border border-gray-200 rounded-lg bg-white" data-testid={testId}>
      <div className="flex items-center gap-1 px-2 py-1.5">
        <button onClick={() => setOpen((o) => !o)} className="flex-1 flex items-center gap-2 text-left px-1 py-1 min-w-0" data-testid={`${testId}-toggle`}>
          {open ? <ChevronDown className="w-3.5 h-3.5 text-gray-400" /> : <ChevronRight className="w-3.5 h-3.5 text-gray-400" />}
          <div className="min-w-0">
            <div className="text-sm font-bold truncate">{title}</div>
            {subtitle && <div className="text-[11px] text-gray-500 truncate">{subtitle}</div>}
          </div>
        </button>
        <IconBtn onClick={onMoveUp} disabled={isFirst} title="Move up" testId={`${testId}-up`}><ArrowUp className="w-3 h-3" /></IconBtn>
        <IconBtn onClick={onMoveDown} disabled={isLast} title="Move down" testId={`${testId}-down`}><ArrowDown className="w-3 h-3" /></IconBtn>
        <IconBtn onClick={onDelete} title="Delete" danger testId={`${testId}-delete`}><Trash2 className="w-3 h-3" /></IconBtn>
      </div>
      {open && (
        <div className="p-3 pt-1 space-y-2 border-t border-gray-100 bg-gray-50/40">
          {children}
        </div>
      )}
    </div>
  );
}

function MiniItem({ isFirst, isLast, onMoveUp, onMoveDown, onDelete, children, testId }) {
  return (
    <div className="border border-gray-200 rounded-md bg-white p-2" data-testid={testId}>
      <div className="flex items-start gap-1">
        <div className="flex-1 min-w-0">{children}</div>
        <div className="flex flex-col gap-0.5">
          <IconBtn onClick={onMoveUp} disabled={isFirst} title="Move up" testId={`${testId}-up`}><ArrowUp className="w-3 h-3" /></IconBtn>
          <IconBtn onClick={onMoveDown} disabled={isLast} title="Move down" testId={`${testId}-down`}><ArrowDown className="w-3 h-3" /></IconBtn>
          <IconBtn onClick={onDelete} title="Delete" danger testId={`${testId}-delete`}><Trash2 className="w-3 h-3" /></IconBtn>
        </div>
      </div>
    </div>
  );
}

function IconBtn({ onClick, disabled, danger, title, children, testId }) {
  return (
    <button
      onClick={onClick}
      disabled={disabled}
      title={title}
      className={`w-6 h-6 rounded-md flex items-center justify-center disabled:opacity-30 ${danger ? "text-red-600 hover:bg-red-50" : "text-gray-600 hover:bg-gray-100"}`}
      data-testid={testId}
    >
      {children}
    </button>
  );
}

function FieldRow({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] font-black uppercase tracking-wider text-gray-600 mb-1">{label}</label>
      {children}
    </div>
  );
}

function AddNewBtn({ label, onClick, testId }) {
  return (
    <button
      onClick={onClick}
      className="w-full py-2 rounded-lg border-2 border-dashed border-emerald-300 text-emerald-700 text-xs font-bold hover:border-emerald-500 hover:text-emerald-800 flex items-center justify-center gap-1.5"
      data-testid={testId}
    >
      <Plus className="w-3.5 h-3.5" /> {label}
    </button>
  );
}

function ImageFieldWithPreview({ value, onChange, testId }) {
  const fileRef = useRef(null);
  const [uploading, setUploading] = useState(false);

  const upload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", file);
      const { data } = await api.post(`/uploads/image`, fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (data?.image_url) onChange(data.image_url);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Couldn't upload image");
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="space-y-1.5">
      {value && (
        <img src={value} alt="" className="w-full aspect-video object-cover rounded-md border border-gray-200" />
      )}
      <div className="flex gap-2">
        <Input value={value || ""} onChange={(e) => onChange(e.target.value)} placeholder="https://…" className="flex-1 text-xs" data-testid={testId} />
        <button
          onClick={() => fileRef.current?.click()}
          disabled={uploading}
          className="flex items-center justify-center px-3 py-1 rounded-md border border-gray-200 hover:bg-gray-50 text-xs font-bold disabled:opacity-50"
          data-testid={`${testId}-upload`}
          title="Upload image"
        >
          {uploading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <ImageIcon className="w-3.5 h-3.5" />}
        </button>
      </div>
      <input type="file" accept="image/*" ref={fileRef} onChange={(e) => upload(e.target.files?.[0])} className="hidden" />
    </div>
  );
}
